/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useRef, useEffect, useState } from 'react';

import useWindowSize from '../../hooks/useWindowSize';
import * as AppPropTypes from '../../lib/PropTypes';

import Button from '../buttons/Button';
import PillButton from '../buttons/Pill';
import DotsIcon from '../icons/Dots';
import Link from '../partials/Link';
import Menu from './Menu';

import styles from '../../../styles/menus/menu-item.module.scss';

const propTypes = {
    item: AppPropTypes.menuItem,
    opened: PropTypes.bool,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    minified: PropTypes.bool,
    large: PropTypes.bool,
    className: PropTypes.string,
};

const defaultProps = {
    item: null,
    opened: false,
    onOpen: null,
    onClose: null,
    minified: false,
    large: false,
    className: null,
};

// FIXME
const BREAKPOINT = 1200;

// FIXME also: I'm copy-pasting code from the Accordion partial to manage the height thing... might need a refactor
// maybe a "useCollapsible" hook that Accordion could also make use of?

const MenuItem = ({ item, opened, onOpen, onClose, minified, large, className }) => {
    const ref = useRef(null);
    const { items: subItems = null, isIndex = false } = item || {};
    const [height, setHeight] = useState(0);
    const { width: windowWidth } = useWindowSize();

    useEffect(() => {
        const bounds = ref.current !== null ? ref.current.getBoundingClientRect() : null;
        const newHeight = opened && bounds ? bounds.height : 0;

        setHeight(newHeight);
    }, [opened, height, setHeight]);

    const onSubMenuClick = useCallback(
        (e, linkType) => {
            if (linkType === 'direct') return;

            e.preventDefault();

            if (opened) onClose(true);
            if (windowWidth < BREAKPOINT && !opened) onOpen(item);
        },
        [opened, windowWidth],
    );

    const onSubMenuItemClick = useCallback(() => {
        if (windowWidth < BREAKPOINT) return;
        if (opened) onClose(true);
    }, [opened, windowWidth]);

    const onMouseEnter = useCallback(() => {
        if (windowWidth >= BREAKPOINT) onOpen(item);
    }, [windowWidth]);

    const onMouseLeave = useCallback(() => {
        if (windowWidth >= BREAKPOINT) onClose();
    }, [windowWidth]);

    return (
        <div
            className={classNames(styles.container, {
                [className]: className !== null,
                [styles.isToggled]: opened,
                [styles.isMinified]: minified,
                [styles.isCurrent]: item !== null && item.active,
                [styles.isLarge]: large,
                [styles.hasSubItems]: subItems !== null && subItems.length > 0,
            })}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {!isIndex ? (
                <Link
                    className={styles.link}
                    href={item.url}
                    onClick={(e) => onSubMenuClick(e, 'direct')}
                >
                    {item.label}
                </Link>
            ) : (
                <Button
                    className={classNames([styles.link, styles.disabled])}
                    onClick={(e) => onSubMenuClick(e, 'expand')}
                >
                    {item.label}
                </Button>
            )}
            {subItems !== null && subItems.length > 0 && (
                <>
                    <PillButton
                        className={styles.expandButton}
                        onClick={(e) => onSubMenuClick(e, 'expand')}
                        icon={<DotsIcon direction={opened ? 'horizontal' : 'vertical'} />}
                    />

                    <div className={styles.subMenuContainer} style={{ height }}>
                        <div ref={ref} className={styles.subMenuHeightWrapper}>
                            <Menu
                                items={subItems}
                                className={styles.subMenu}
                                itemsClassName={styles.subItems}
                                itemClassName={styles.subItem}
                                linkClassName={styles.subLink}
                                activeItemClassName={styles.isSubLinkCurrent}
                                onClick={onSubMenuItemClick}
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

MenuItem.propTypes = propTypes;
MenuItem.defaultProps = defaultProps;

export default MenuItem;
